import FormStyleProvider from "@client/content/Providers/FormStyle/container";
import { ImageEntity } from "@client/content/controls/Image/container";
import { getEntityRef, getRootContainerProps } from "@client/content/lib/helper";
import ProductPrice, { type Price } from "@client/content/modules/ProductList/Content/Item/ProductPrice";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { Alert } from "@client/controls/Alert/component";
import { FormField } from "@client/controls/Form/Field/component";
import Heading, { type HeadingProps } from "@client/controls/HTML/Heading/component";
import Select from "@client/controls/Inputs/Select/component";
import Link from "@client/controls/Link/container";
import { areEqual } from "@client/lib/array";
import { serializeValue } from "@client/lib/values/size";
import { Dimension } from "@client/types";
import ButtonsContainer from "@common/controls/Container/Buttons";
import SubmitButton from "@common/controls/Form/Buttons/Submit";
import { FormControlled, type FormControlledRenderParams } from "@common/controls/Form/component";
import NumberInput from "@common/controls/Inputs/Number/component";
import { IconType, SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { identityPromise } from "@common/lib/misc";
import classNames from "classnames";
import { first } from "lodash";
import * as React from "react";
import { type ProductDetail } from "../types";
import Reviews from "./Reviews/container";
import SubscriptionPrice from "./SubscriptionPrice/component";
import { getIsOptionValueAvailable } from "./lib";

type ElementType = "price" | "quantity" | "info" | "social";
export type MapProps = RootContainerProps & {
  addToCartCaption: string;
  apiInfo: ModuleApiInfo;
  buyNowCaption: string;
  descriptionStyle: React.CSSProperties;
  cartUrl: string;
  elements: ElementType[];
  hasCart: boolean;
  isEditMode: boolean;

  currencyCode: string;
  nameHeading: HeadingProps;
  noImageUrl: string;
  oldPriceColor?: string;
  outOfStockStyle: React.CSSProperties | undefined;
  product: ProductDetail.Product;
  productVariants: ProductDetail.ProductVariant[];
  regularPriceStyle: React.CSSProperties;
  salePriceStyle: React.CSSProperties;
  socialIconsId: string | null;
  shouldDisplayTax: boolean;
  status: ProductDetail.Status;
  StaticContainer: React.ComponentType<RootContainerProps>;
};

export interface ActionProps {
  addToCart: (data: ProductDetail.View.AddToCart) => void;
  buyNow: (data: ProductDetail.View.AddToCart) => void;
}

type FormData = {
  quantity: number;
  optionValueIds: string[];
};

export default (props: ActionProps & MapProps) => {
  const i18n = useI18n();

  const [variant, setVariant] = React.useState<ProductDetail.Api.ProductVariant>();

  React.useEffect(() => {
    if (props.productVariants.length === 1) setVariant(props.productVariants[0]);
  }, [props.productVariants]);

  const [imageUrl, setImageUrl] = React.useState<string | null>();

  React.useEffect(
    () =>
      setImageUrl(
        variant ? variant.displayImageUrl : first(props.productVariants.find((x) => x.imageUrls.length)?.imageUrls)
      ),
    [variant, props.productVariants]
  );

  const onChange = React.useCallback(
    (name: keyof FormData, value: any) => {
      if (name === "optionValueIds") {
        setVariant(props.productVariants.find((x) => areEqual(value, x.optionValueIds)));
      }
    },
    [props.productVariants, props.product.options]
  );

  const price = React.useMemo(
    (): Price => ({
      currencyCode: props.currencyCode,
      price: variant?.price ?? 0,
      salesPrice: variant?.salesPrice ?? 0
    }),
    [props.currencyCode, variant]
  );

  const thumbnailImageSize = React.useMemo(
    () =>
      serializeValue({
        dimension: Dimension.Height,
        dimensionSize: 70,
        isLocked: true,
        height: 70,
        width: 70,
        useAspectRatio: true
      }),
    []
  );

  const thumbnailImageStyle = React.useMemo(
    (): React.CSSProperties => ({
      objectFit: "cover"
    }),
    []
  );

  const defaultValues = React.useMemo(
    (): FormData => ({
      optionValueIds: [],
      quantity: 1
    }),
    [props.product.options, props.productVariants]
  );

  return (
    <FormStyleProvider {...getEntityRef(props)}>
      <div className="product">
        <div className="top">
          <div className="images">
            <div className="cover">
              <ImageEntity
                {...getEntityRef(props)}
                src={imageUrl ?? props.noImageUrl}
                style={thumbnailImageStyle}
                useMaxValues={false}
              />
            </div>
            {variant && variant.imageUrls.length > 1 && (
              <div className="gallery">
                {variant.imageUrls.map((x) => (
                  <div
                    key={x}
                    className={classNames("item", { active: x === imageUrl })}
                    onClick={() => setImageUrl(x)}
                  >
                    <ImageEntity
                      {...getEntityRef(props)}
                      key={x}
                      size={thumbnailImageSize}
                      src={x}
                      style={thumbnailImageStyle}
                      isFixedSize
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="info">
            <Heading className="product-name" {...props.nameHeading} content={props.product.name} />

            <FormControlled onChange={onChange} defaultValues={defaultValues} submit={identityPromise}>
              {({ onChange, values }: FormControlledRenderParams<FormData>) => (
                <>
                  {props.product.options.slice(0, values.optionValueIds.length + 1).map((x, i) => (
                    <FormField
                      key={x.id}
                      isRequired
                      name={`optionValueIds[${i}]`}
                      Component={Select}
                      componentProps={{
                        options: x.values.map((x) => ({
                          label: x.name,
                          value: x.id,
                          isDisabled: !getIsOptionValueAvailable(props.productVariants, values.optionValueIds, i, x.id)
                        }))
                      }}
                      onChange={(name, value) => {
                        const newOptionValueIds = values.optionValueIds.slice(0, i);
                        newOptionValueIds[i] = value;
                        onChange("optionValueIds", newOptionValueIds);
                      }}
                      label={x.name}
                      value={values.optionValueIds[i]}
                    />
                  ))}
                  {/* <pre>{JSON.stringify(values, null, 2)}</pre> */}

                  {!!variant && (
                    <>
                      {props.elements.includes("info") && !!variant.description && (
                        <div
                          className="description"
                          style={props.descriptionStyle}
                          dangerouslySetInnerHTML={{ __html: variant.description }}
                        />
                      )}

                      {variant.availableQuantity ? (
                        props.elements.includes("quantity") ? (
                          <FormField
                            name="quantity"
                            isRequired
                            label={i18n.quantity}
                            Component={NumberInput}
                            componentProps={{ min: 1, max: variant.availableQuantity }}
                            value={values.quantity}
                          />
                        ) : null
                      ) : (
                        <div className="unavailable" style={props.outOfStockStyle}>
                          {i18n.outOfStock}
                        </div>
                      )}

                      {props.elements.includes("price") &&
                        (variant.subscriptionInfo ? (
                          <SubscriptionPrice
                            currencyCode={props.currencyCode}
                            info={variant.subscriptionInfo}
                            oldPriceColor={props.oldPriceColor}
                            regularPriceStyle={props.regularPriceStyle}
                            salePriceStyle={props.salePriceStyle}
                            taxPercentage={props.shouldDisplayTax ? variant.taxPercentage : 0}
                          />
                        ) : (
                          <ProductPrice
                            oldPriceColor={props.oldPriceColor}
                            price={price}
                            regularPriceStyle={props.regularPriceStyle}
                            salePriceStyle={props.salePriceStyle}
                            taxPercentage={props.shouldDisplayTax ? variant.taxPercentage : 0}
                          />
                        ))}
                    </>
                  )}

                  <ButtonsContainer align="left">
                    <SubmitButton
                      isDisabled={
                        props.status === "AddingToCart" || !variant || !variant?.availableQuantity || props.isEditMode
                      }
                      onClick={() => props.addToCart({ quantity: values.quantity, variantId: variant!.id })}
                    >
                      {props.addToCartCaption}
                    </SubmitButton>
                    <SubmitButton
                      isDisabled={
                        props.status === "AddingToCart" || !variant || !variant?.availableQuantity || props.isEditMode
                      }
                      onClick={() => props.buyNow({ quantity: values.quantity, variantId: variant!.id })}
                    >
                      {props.buyNowCaption}
                    </SubmitButton>
                  </ButtonsContainer>
                </>
              )}
            </FormControlled>

            {props.socialIconsId && props.elements.includes("social") && (
              <props.StaticContainer {...getRootContainerProps(props)} id={props.socialIconsId} />
            )}

            {props.hasCart && (
              <Alert color={SystemColor.Green} icon={IconType.Check}>
                {i18n.added} <Link to={props.cartUrl}>{i18n.viewCart}</Link>
              </Alert>
            )}
          </div>
        </div>

        <Reviews {...getRootContainerProps(props)} />
      </div>
    </FormStyleProvider>
  );
};
